import { useEffect, useState } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import moment from 'moment';

import { UseStaySelectionParams } from '../types';

// Typing a move in date fires a change per date section, so wait for the user to pause before searching
const SEARCH_DEBOUNCE_MS = 400;

export const useStaySelection = ({
    initialMoveInDate,
    initialStayDuration,
    appliedMoveInDate,
    appliedStayDuration,
    urlError,
    applySearch,
    clearSearch,
}: UseStaySelectionParams) => {
    const [selectedMoveInDate, setSelectedMoveInDate] = useState<string | null>(initialMoveInDate);
    const [selectedStayDuration, setSelectedStayDuration] = useState<number | null>(
        initialStayDuration
    );
    const [selectionError, setSelectionError] = useState<string | null>(null);
    const { key: locationKey } = useLocation();
    const navigationType = useNavigationType();

    // The URL only seeds the selection, so Back/Forward would leave the selection out of step with the
    // applied search and the auto search below would re-apply it, undoing the navigation. Follow the URL
    // on history navigation instead. Declared before the URL error effect so that still wins on an invalid URL
    useEffect(() => {
        if (navigationType !== NavigationType.Pop) {
            return;
        }

        setSelectedMoveInDate(appliedMoveInDate);
        setSelectedStayDuration(appliedStayDuration);
        setSelectionError(null);
        // Only on navigation; the applied values are read from the same URL so are current here
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationKey, navigationType]);

    // An invalid search in the URL is surfaced once as a selection error, then dropped from the URL
    useEffect(() => {
        if (!urlError) {
            return;
        }

        setSelectionError(urlError);
        setSelectedMoveInDate(null);
        setSelectedStayDuration(null);
        clearSearch();
    }, [urlError, clearSearch]);

    const hasSelectedDates = selectedMoveInDate !== null && selectedStayDuration !== null;

    // A complete, valid selection that hasn't been applied to the URL (and so fetched) yet
    const isSearchPending =
        hasSelectedDates &&
        moment(selectedMoveInDate, 'YYYY-MM-DD', true).isValid() &&
        (selectedMoveInDate !== appliedMoveInDate || selectedStayDuration !== appliedStayDuration);

    useEffect(() => {
        if (!isSearchPending) {
            return undefined;
        }

        const timeout = setTimeout(
            () => applySearch(selectedMoveInDate as string, selectedStayDuration as number),
            SEARCH_DEBOUNCE_MS
        );

        return () => clearTimeout(timeout);
    }, [isSearchPending, selectedMoveInDate, selectedStayDuration, applySearch]);

    const handleMoveInDateChange = (date: string | null) => {
        setSelectedMoveInDate(date);
        setSelectionError(null);

        if (!date) {
            setSelectedStayDuration(null);
            clearSearch();
        }
    };

    const handleDurationValidityChange = (isValid: boolean) => {
        if (!isValid) {
            setSelectedStayDuration(null);
            setSelectionError(null);
            clearSearch();
        }
    };

    const handleStayDurationChange = (duration: number | null) => {
        setSelectedStayDuration(duration);

        if (duration !== null) {
            setSelectionError(null);
        }
    };

    return {
        selectedMoveInDate,
        selectedStayDuration,
        selectionError,
        hasSelectedDates,
        isSearchPending,
        setSelectionError,
        handleMoveInDateChange,
        handleDurationValidityChange,
        handleStayDurationChange,
    };
};
